/* eslint-disable */
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationExtras, ParamMap, Params, Router } from '@angular/router';
import { OvAutoService } from '@ov-suite/services';
import {
  Constructor,
  FieldMetadata,
  GenericHierarchy,
  getFieldMetadata,
  getSearchableMetadata,
  getSearchMetadata,
  OVSearchData,
  PageReturn,
  SearchableMetadata,
} from '@ov-suite/ov-metadata';
import {
  ColumnData,
  ColumnDataButton,
  parseParameters,
  QueryParam,
  QueryParams,
  RowData
} from '@ov-suite/helpers-shared';
import { hasPermissionByFeature, PermissionAction, verifyPermission } from '@ov-suite/authguard-angular';
import { cloneDeep } from 'lodash';
import { FilterChange } from '../table/table.component';
import { DateRange } from '@ov-suite/ui';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '@ov-suite/authguard-angular/lib/authentication/authentication.service';
import { tryParse } from '@ov-suite/helpers-angular/lib/try-parse.helper';

type GenericHierarchyType = GenericHierarchy;

@Component({
  selector: 'ov-suite-hierarchy-table',
  templateUrl: './hierarchy-list.component.html',
  styleUrls: ['./hierarchy-list.component.scss'],
})
export class HierarchyListComponent<T extends GenericHierarchyType> implements OnInit {

  loaded = false;

  @Input() titleReplace?: unknown;

  @Input() overrideColumnData?: ColumnData<T>[];

  @Input() rowData: RowData<T>;

  // To Be deprecated
  @Input() ovAutoService: OvAutoService;

  @Input() title: string;

  @Input() formClass: Constructor<T>;

  @Input() hasBulkUpload = true;

  @Input() hasBulkExport = true;

  @Input() excludeColumns = 1;

  @Input() showTopBar = false;

  @Input() filterEnabled = true;

  @Input() showPageSelect = true;

  @Input() pageChangeEnabled = true;

  @Input() selectableRows = false;

  @Input() clickableRows = false;

  @Input() hideAddButton = false;

  @Input() showFiller = true;

  @Input() showScroll = false;

  @Input() editableRows = false;

  @Input() dropdownData = {};

  @Input() overrideServiceMethod = null;

  @Input() enableRefresh = false;

   _dataOverride: T[];

  @Input() set dataOverride(data: T[]) {
    this._dataOverride = data;
    this.getData('data override');
  }

  getDataOverride () {
    return this._dataOverride;
  }

  @Input() emptyComponent;

  @Input() bulkActionComponent;

  @Input() searchDropdownPosition?: string;

  @Input()
  set reFetch(reFetch: number) {
    if (reFetch > 0 && this.loaded) {
      this.getData('refetch');
    }
  }

  @Output() itemSelect = new EventEmitter<T[]>();

  @Output() onDeleteItem = new EventEmitter<Record<string, unknown>>();

  @Output() editedItems = new EventEmitter<T[]>();

  @Output() executedBulkUpload = new EventEmitter();

  @Output() originalData = new EventEmitter<T[]>();

  @Input() overrideAddButtonClick = false;

  @Output() addButtonClick = new EventEmitter<T>();

  @Output() rowClick = new EventEmitter<T>();

  loading = false;

  customizing = false;

  selected: T[];

  _idDefaultSearchFilter: Record<string, QueryParams[]>;

  _idFilter: Record<string, QueryParams[]>;

  _hideColumnKeys: string[] = [];

  updatedItems: Record<number, { newItem: T; oldItem: T }>;

  @Input() hasDeletePermission = true;

  @Input() hasEditPermission = true;

  @Input() hasCreatePermission = true;

  @Input() defaultOrderDirection: 'ASC' | 'DESC' = 'ASC';

  @ViewChild('uploadComponent') uploadComponent: ElementRef;

  _defaultQuery: Record<string, QueryParams[]>;

  @Input()
  set defaultQuery(query: Record<string, QueryParams[]>) {
    this._defaultQuery = query;
    if (this.loaded) {
      this.getData('default query');
    }
    // this.setParam({});
  }

  get defaultQuery() {
    return this._defaultQuery;
  }

  @Input()
  set filter(event) {
    this._idFilter = event;
  }

  @Input()
  set defaultSearchFilter(event) {
    this._idDefaultSearchFilter = event;
  }

  get defaultSearchFilter() {
    return this._idDefaultSearchFilter;
  }

  get filter() {
    return this._idFilter;
  }

  @Input()
  set hideColumnKeys(event: string[]) {
    this._hideColumnKeys = event;
  }

  get hideColumnKeys() {
    return this._hideColumnKeys;
  }

  data: T[] = [];

  originalDataCopy: T[] = [];

  // Paging Variables
  totalCount = 0;

  _page = 0;

  set page(page: number) {
    this._page = page;
    // this.setParam({ [queryParams.PAGE]: page });
  }

  get page() {
    return this._page;
  }

  pageSize = 10;

  searchableMetadata: SearchableMetadata;

  searchMetadata: OVSearchData<T>;

  // End Paging Variables
  _parentId: number | null = null;

  set parentId(num: number) {
    this._parentId = num ?? null;
    // this.setParam({ [queryParams.PARENT_ID]: this._parentId });
  }

  get parentId() {
    return this._parentId;
  }

  metadata: FieldMetadata<T>;

  searchMap: Record<string, QueryParams[]> = {};

  filterMap: Record<string, QueryParams[]> = {};

  queryMap: Record<string, QueryParams[]> = {};

  currentFilterChange: FilterChange;

  @Input() orderColumn = 'id';

  orderDirection: 'ASC' | 'DESC' = this.defaultOrderDirection;

  @Input() import: () => void = () => {};

  queryParams: Params | null = {};

  advancedSearchFields: ColumnData<T>[];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly autoService: OvAutoService,
    public readonly ngbModal: NgbModal,
    private readonly authenticationService: AuthenticationService,
  ) {
    this.route.data.subscribe(perm => {
      if (perm?.feature?.id) {
        const user = authenticationService.user.value;
        hasPermissionByFeature(user, perm.feature.id).then(permission => {
          this.hasDeletePermission = verifyPermission(permission, PermissionAction.delete);
          this.hasEditPermission = verifyPermission(permission, PermissionAction.update);
          this.hasCreatePermission = verifyPermission(permission, PermissionAction.create);
        });
      }
    });
  }

  setParams(param: Record<string, string | number | null>) {
    this.queryParams = this.evaluateQueryParams(param);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.queryParams,
      skipLocationChange: false,
      replaceUrl: true,
      queryParamsHandling: '',
    } as NavigationExtras);
  }

  /**
   * Evaluates the values set in the operations of filterChange()
   * a null value indicates the removal of a filter item.
   * @param param
   */
  evaluateQueryParams(param: Record<string, string | number | null>) {
    Object.keys(param).forEach(key => {
      if (param[key] === null) {
        delete this.queryParams[key];
      } else {
        this.queryParams[key] = param[key];
      }
    })

    return this.queryParams;
  }

  private clearFilterQueryParams() {
    Object.keys(this.queryParams ?? {}).forEach(key => {
      if (
        key.startsWith(`${QueryParam.filter}:`) ||
        key.startsWith(`${QueryParam.search}:`) ||
        key.startsWith(`${QueryParam.query}:`)
      ) {
        delete this.queryParams[key];
      }
    });
  }

  pageSizeInput(pageSize: number) {
    if (pageSize > 0) {
      this.setParams({ [QueryParam.pageSize]: pageSize });
    }
  }

  changePage(page: number): void {
    this.setParams({ [QueryParam.page]: page });
  }

  refresh($refresh: boolean) {
    if ($refresh) {
      this.getData(' refresh');
    }
  }

  getData(source?: string): void {
    console.info({ source });
    if (this.dataOverride) {
      this.data = this.dataOverride;
      this.totalCount = this.dataOverride.length;
      this.loading = false;
      this.loaded = true;
      this.originalData.emit(this.data);

      return;
    }
    const metadata = getFieldMetadata<T>(this.formClass);
    if (metadata.fields.some(f => f.propertyKey === 'parent')) {
      this._idFilter = {
        ...this._idFilter,
        parentId: [this.parentId || null],
      };
    }

    const keys: Set<string> = new Set(['id']);

    this.metadata = metadata;

    if(this.getRowData()['keys']){
      this.getRowData()['keys']?.forEach(key => {
        keys.add(key);
      })
    }

    this.getColumnData().forEach(col => {
      switch (col.type) {
        case 'other':
        case 'pills':
        case 'button-bar':
        case 'buttons':
          col.keys?.forEach(key => keys.add(key));
          break;
        case 'column':
          col.keys?.forEach(key => keys.add(key));
          keys.add(col.key as string);
          break;
        default:
          if (col.key) {
            keys.add(col.key as string);
          }
          col.keys?.forEach(key => keys.add(key));

      }
    });

    this.loading = true;

    const handlePromise = (promise: Promise<PageReturn<T>>) => {
      promise
        .then(result => {
          this.data = result.data;
          this.originalDataCopy = cloneDeep(result.data);
          this.totalCount = result.totalCount;
          this.loading = false;
          this.originalData.emit(this.data);
        })
        .catch((err) => {
          console.error(err);
          this.loading = false;
        });
    };

    const baseParams = {
      search: {
        ...this._idDefaultSearchFilter,
        ...this.searchMap,
      },
      filter: this.filterMap,
      query: {
        ...this.defaultQuery,
        ...this._idFilter,
        ...this.queryMap,
      },
      limit: this.pageSize,
      offset: this.page * this.pageSize,
      orderDirection: this.orderDirection,
      orderColumn: this.orderColumn,
      keys: [...keys],
    };

    handlePromise(this.autoService.list({ ...baseParams, entity: this.formClass }));
  }

  parseFilterParameter(paramMap: ParamMap): Record<string, QueryParams[]> {
    const keys = paramMap.keys.filter(key => !key.startsWith('_')).map(param => param.split(':').join());
    const parseValue = (input: string): QueryParams => {
      if (input === 'true')
        return {
          operator: '=',
          value: true,
        };
      if (input === 'false')
        return {
          operator: '=',
          value: false,
        };
      if (!Number.isNaN(Number(input))) return Number(input);
      return input;
    };

    const outputMap: Record<string, QueryParams[]> = {};

    keys.forEach(key => {
      const value = paramMap.get(key);
      if (!value) {
        return;
      }
      if (outputMap[key]?.length) {
        outputMap[key].push(parseValue(value));
      } else {
        outputMap[key] = [parseValue(value)];
      }
    });

    return outputMap;
  }

  loadFromRoute() {
    this.route.queryParamMap.subscribe(params => {
      this.parentId = params.get(QueryParam.parentId) ? Number(params.get(QueryParam.parentId)) : null;
      this.pageSize = params.get(QueryParam.pageSize) ? Number(params.get(QueryParam.pageSize)) : 10;
      this.page = Number(params.get(QueryParam.page) ?? 0);
      this.orderColumn = params.get(QueryParam.orderColumn) ?? this.orderColumn ?? 'id';
      this.orderDirection = (params.get(QueryParam.orderDirection) as 'ASC' | 'DESC') ?? this.orderDirection ?? 'ASC';

      this.filterMap = parseParameters(QueryParam.filter, params);
      this.searchMap = parseParameters(QueryParam.search, params);
      this.queryMap = parseParameters(QueryParam.query, params);
      // this._idFilter = { ...this._idFilter, ...queryKeys };
      // this.filterHistory = { ...filterKeys, ...queryKeys };
      // this.currentFilterChange = {
      //   filter: filterKeys,
      //   query: queryKeys,
      //   search: this.searchMap,
      // };

      // Query Map is the one that holds the dates
      Object.keys(this.queryMap).forEach(key => {
        const values = this.queryMap[key];

        values.forEach(value => {
          this.queryMap[key] = [tryParse(value) as QueryParams];
        })
      })

      this.getData('load from route');
    });

    // Set the query params on first load of the page so that we have a record that we can manipulate, thereafter it is tracked and changed via setParams().
    this.queryParams = {
      ...(this.parentId != null ? { [QueryParam.parentId]: this.parentId } : {}),
      ...(this.pageSize != null ? { [QueryParam.pageSize]: this.pageSize } : {}),
      ...(this.page != null ? { [QueryParam.page]: this.page } : {}),
      ...(this.orderColumn != null ? { [QueryParam.orderColumn]: this.orderColumn } : {}),
      ...(this.orderDirection != null ? { [QueryParam.orderDirection]: this.orderDirection } : {}),
      ...this.filterMap,
      ...this.searchMap,
      ...this.queryMap,
    }
  }

  ngOnInit() {
    this.orderDirection = this.defaultOrderDirection;
    this.loadFromRoute();
    this.getMetadata();

    // Sets the fields that can be filtered on in the advanced search component;
    if (this.overrideColumnData) {
      this.advancedSearchFields = this.overrideColumnData;
    } else {
      this.advancedSearchFields = this.metadata.table;
    }

    this.searchableMetadata = getSearchableMetadata(this.formClass);
    this.searchMetadata = getSearchMetadata(this.formClass);
  }

  getMetadata(): void {
    const metadata = getFieldMetadata<T>(this.formClass);
    const extraFields: ColumnData<T>[] = [];
    if (this.excludeColumns >= 1) {
      const buttons = [];
      const editButton = {
        tooltip: 'Edit',
        classes: 'btn-primary btn-sm fa pt-1 pb-1 fa-pencil',
        action: (item: T) => {
          this.edit(item);
        },
      };
      const deleteButton = {
        tooltip: 'Delete',
        classes: 'btn-primary ml-1 btn-sm fa fa-trash-o',
        action: async (item: T) => {
          await this.delete(item);
        },
      };

      if (this.excludeColumns === 1) {
        if (this.hasEditPermission) {
          buttons.push(editButton);
        }

        if (this.hasDeletePermission) {
          buttons.push(deleteButton);
        }
      }

      if (this.excludeColumns === 2) {
        if (this.hasDeletePermission) {
          buttons.push(deleteButton);
        }
      }

      if (this.excludeColumns === 3) {
        if (this.hasEditPermission) {
          buttons.push(editButton);
        }
      }

      if (this.excludeColumns < 4 && buttons.length) {
        extraFields.push({
          type: 'buttons',
          title: 'Fast Actions',
          buttons,
          keys: [],
          disableFiltering: true,
          disableSorting: true,
          id: 'fast_actions',
        });
      }
    }
    if (this.excludeColumns === 0) {
      extraFields.push({
        type: 'status',
        title: 'Status',
        key: 'status',
        id: 'status',
      });
    }

    this.metadata = { ...metadata, table: [...metadata.table, ...extraFields] };
    // this.hideColumnKeys = this.metadata.table.filter(item => !!item.hideColumnKey).map(item => item.hideColumnKey);
  }

  onSearchChange(searchChange: Record<string, string> | null): void {
    if (!searchChange) {
      this.searchMap = {};
      this.page = 0;
      this.getData('search cleared');
      return;
    }

    Object.keys(searchChange).forEach(key => {
      this.searchMap[key] = [searchChange[key]];
    })

    this.page = 0;
    this.getData('search');
  }

  search(text: string): void {
    let filter = '';
    const { name } = this.metadata; // asdasd
    this.searchMetadata?.searchKeys.forEach(key => {
      this.searchMap[key as string] = [text];

      if (filter) {
        filter += ` OR "${name}"."${String(key)}" ILIKE '%${text}%'`;
      } else {
        filter = `"${name}"."${String(key)}" ILIKE '%${text}%'`;
      }
    });

    this.page = 0;
    this.getData('search');
  }

  onAdvancedFilterChange(filter: FilterChange) {
    this.currentFilterChange = filter;
    this.filterChange(filter);
  }

  customize() {
    this.customizing = !this.customizing;
  }

  add(): void {
    const options: NavigationExtras = {
      queryParams: {},
    };

    if (this.parentId) {
      options.queryParams._parentId = this.parentId;
    }
    if (this.page) {
      options.queryParams._prevPage = this.page;
    }

    if (this.overrideAddButtonClick) {
      this.addButtonClick.emit(null);
    } else {
      this.router.navigate([this.router.url.slice(1).split('?')[0], 'add'], options);
    }
  }

  edit(item) {
    const options: NavigationExtras = {
      queryParams: { id: item.id },
    };
    if (this.page) {
      options.queryParams._prevPage = this.page;
    }
    this.router.navigate([this.router.url.slice(1).split('?')[0], 'edit'], options);
  }

  async delete(item) {
    if (window.confirm('Are you sure you want to delete this?')) {
      await this.autoService.delete(this.formClass, item?.id).then(() => {
        this.onDeleteItem.emit(item);
      });
      this.getData('delete');
    }
  }

  select = (item: T) => {
    const options: NavigationExtras = {
      queryParams: {
        _parentId: item.id,
      },
    };

    this.router.navigate([this.router.url.slice(1).split('?')[0]], options);
  };

  back = () => {
    if (this.parentId) {
      this.location.back();
    }
  };

  onItemSelected(event: T[]) {
    this.selected = event;
    this.itemSelect.emit(event);
  }

  filterChange(change: FilterChange) {
    this.clearFilterQueryParams();
    this.setParams({
      [QueryParam.page]: 0,
      ...this.flattenFilterValues(change.filter),
      ...this.flattenSearchValues(change.search),
      ...this.flattenQueryValues(change.query),
    });
  }

  private flattenFilterValues(values: Record<string, QueryParams[]>): Record<string, string | number | null> {
    return Object.entries(values ?? {}).reduce<Record<string, string | number | null>>((acc, [key, value]) => {
      acc[key] = value?.length ? (value[0] as string | number) : null;
      return acc;
    }, {});
  }

  private flattenSearchValues(values: Record<string, QueryParams[]>): Record<string, string | number | null> {
    return Object.entries(values ?? {}).reduce<Record<string, string | number | null>>((acc, [key, value]) => {
      acc[key] = value?.length ? (value[0] as string | number) : null;
      return acc;
    }, {});
  }

  private flattenQueryValues(values: Record<string, QueryParams[]>): Record<string, string | number | null> {
    return Object.entries(values ?? {}).reduce<Record<string, string | number | null>>((acc, [key, value]) => {
      const queryValue = value?.[0];

      if (queryValue && typeof queryValue === 'object' && queryValue['type'] === 'date-range') {
        const dateRange = queryValue as unknown as DateRange;
        acc[key] = dateRange.from && dateRange.to ? JSON.stringify(dateRange) : null;
        return acc;
      }

      acc[key] = value?.length ? (value[0] as string | number) : null;
      return acc;
    }, {});
  }

  orderChange(order: { column: string; direction: 'ASC' | 'DESC'; data: ColumnData<unknown> }) {
    this.setParams({
      [QueryParam.orderColumn]: order.data.orderKey,
      [QueryParam.orderDirection]: order.direction,
    });
  }

  isObject(object): object is Object {
    return object != null && typeof object === 'object';
  }

  isObjectEqual(object1: Object, object2: Object) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];
      const areObjects = this.isObject(val1) && this.isObject(val2);
      if ((areObjects && !this.isObjectEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
        return false;
      }
    }

    return true;
  }

  onItemEdit(item: T) {
    const original = this.originalDataCopy.find(it => it.id === item.id);

    if (Object.keys(item).some(key => key === 'hasChanged' || key === 'isEditable' || key === 'newId')) {
      if (Object.keys(item).some(key => key === 'hasChanged')) {
        delete item['hasChanged'];
      }
      if (Object.keys(item).some(key => key === 'isEditable')) {
        delete item['isEditable'];
      }
      if (Object.keys(item).some(key => key === 'newId')) {
        delete item['newId'];
      }
      if (Object.keys(item).some(key => key === 'isSelected')) {
        delete item['isSelected'];
      }
    }

    if (!this.isObjectEqual(original, item)) {
      this.updatedItems = { [item.id]: { newItem: item, oldItem: original }, ...this.updatedItems };
      const updatedList = this.convertUpdatedItemsToArray(this.updatedItems);
      this.editedItems.emit(updatedList);
    }
  }

  convertUpdatedItemsToArray(data: Record<string, unknown>) {
    const results: T[] = [];
    for (const updatedItemsKey of Object.keys(data)) {
      const item = this.updatedItems[updatedItemsKey];
      results.push(item.newItem);
    }
    return results;
  }

  uploaded(uploaded: boolean) {
    if (uploaded) {
      this.getData('uploaded');
      this.executedBulkUpload.emit();
    }
  }

  public setFilters(input: Record<string, unknown>): void {
    const change: FilterChange = {
      filter: {},
      query: {},
      search: {},
    };
    Object.entries(input).forEach(([key, value]) => {
      change.filter[key] = [`${value}`];
    });
    this.filterChange(change);
  }

  hasFilters(): boolean {
    return Object.keys(this.filterMap).length > 0 || Object.keys(this.searchMap).length > 0 || Object.keys(this.queryMap).length > 0;
  }

  onRowClick(item: T): void {
    this.rowClick.emit(item);
  }

  getColumnData () {
    return this.overrideColumnData ?? this.metadata?.table ?? [];
  }

  getRowData () {
    return this.rowData ?? {};
  }

  onRowItemClick(eventData): void {
    if (eventData.col && eventData.col.type === 'other') {
      const colKeys: string[] = eventData.col.keys;
      const found = this.getColumnData().some((item) => {
        if (item.type !== 'other') {
          return false;
        }
        const itemKeys: string[] = [];
        if (Array.isArray(item.keys)) {
          itemKeys.push(...item.keys);
        }
        return colKeys.some(t => {
          return itemKeys.includes(t);
        });
      });
      if (found) {
        eventData.col.rowItemAction?.(eventData.data, { ngbModal: this.ngbModal, apiService: this.autoService });
      }
    }
  }

  onRowItemButtonClick(eventData) {
    switch (eventData.event.actionType) {
      case 'edit':
        this.edit(eventData.data);
        break;
      case 'delete':
        this.delete(eventData.data);
        break;
      case 'modal':
        eventData.event.action(eventData.data, { ngbModal: this.ngbModal });
        break;
      case 'custom':
        // TODO custom action
        // console.assert('custom action not yet implemented');
        break;
      case 'routeLink':
        this.routerLink(eventData.event, eventData.data);
        break;
      default:
        if (typeof eventData.event.action === 'function') {
          eventData.event.action(eventData.data, { apiService: this.autoService, ngbModal: this.ngbModal });
        }
        break;
    }
  }

  routerLink(button: ColumnDataButton<T>, data: T): void {
    if (!button.routerLink) {
      return;
    }
    const { url } = this.router;
    const options: NavigationExtras = {
      queryParams: button.queryParams ? button.queryParams(data, url) : {},
    };
    this.router.navigate(button.routerLink(data, url), options);
  }
}
